import {gql, QueryResult} from '@apollo/client';
import {DocumentNode} from 'graphql';
import React, {useState} from 'react';
import isEqual from 'react-fast-compare';

import WorkspaceItemInaccessibleGuard from 'auth/WorkspaceItemInaccessibleGuard';
import BenchlingConfig from 'BenchlingConfig';
import CollaborationManagementSection from 'collaborator-components/CollaborationManagementSection';
import useCollaborationManagementState, {
  Collaboration,
} from 'collaborator-components/useCollaborationManagementState';
import {Type as FolderType} from 'constants/FolderConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import deepCopy from 'lib/deepCopy';
import notifications from 'notifications';
import getTestProps from 'test/lib/cypress/getTestProps';
import Col from 'ui-library/bs/Col';
import FormGroup from 'ui-library/bs/FormGroup';
import FormInput from 'ui-library/bs/FormInput';
import Label from 'ui-library/bs/Label';
import Row from 'ui-library/bs/Row';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import Modal from 'ui-library/modals/Modal';
import {omit} from 'util/omitKey';
import {ElementMouseEvent, InputChangeEvent} from 'util/Types';

import {
  FolderSettingsModalQuery,
  FolderSettingsModalQueryVariables,
} from './__generated/FolderSettingsModalQuery';
import * as FolderActions from './FolderActions';
import FolderSelector from './FolderSelector';

type GqlFolder = ExtractByTypename<FolderSettingsModalQuery['folder'], 'FolderNode'>;

export const FOLDER_SETTINGS_MODAL_QUERY: DocumentNode = gql`
  query FolderSettingsModalQuery($folderId: StrictString!) @skipAudit @engComponent(name: PROJECT_FOLDER) {
    folder(id: $folderId) {
      ... on FolderNode {
        id
        name
        description
        type
        parent {
          ... on FolderNode {
            id
          }
        }
        source {
          ... on SourceInterface {
            id
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

interface FolderSettingsModalWrapperProps {
  folderId: string;
  onRequestClose: () => void;
  isReadOnly: boolean;
}

interface FolderSettingsModalProps {
  folder: Folder;
  onRequestClose: () => void;
  refetch: QueryResult['refetch'];
  isReadOnly: boolean;
}

export type Folder = Omit<GqlFolder, '__typename' | 'source' | 'parent'> & {
  parentId: string;
};

export function FolderSettingsModalWrapper({
  folderId,
  onRequestClose,
  isReadOnly,
}: FolderSettingsModalWrapperProps): JSX.Element | null {
  const result = useBenchlingQuery<FolderSettingsModalQuery, FolderSettingsModalQueryVariables>(
    FOLDER_SETTINGS_MODAL_QUERY,
    {
      variables: {folderId},
    }
  );

  if (result.type === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber />;
  } else if (result.type === QueryResultType.SUCCESS) {
    const {folder} = result.data;
    if (isInaccessible(folder)) {
      return (
        <WorkspaceItemInaccessibleGuard inaccessibleInfo={folder} modelType={ModelTypeConstants.FOLDER} />
      );
    } else {
      return (
        <FolderSettingsModal
          folder={{
            ...omit(folder, '__typename', 'source', 'parent'),
            parentId: folder.parent == null ? folder.source.id : folder.parent.id,
          }}
          onRequestClose={onRequestClose}
          refetch={result.refetch}
          isReadOnly={isReadOnly}
        />
      );
    }
  } else {
    return null;
  }
}

type FolderWithCollaborations = Folder & {
  collaborations: Array<Collaboration>;
};

const _getUpdatedFolderObject = (
  initialFolder: FolderWithCollaborations,
  folder: FolderWithCollaborations
): Partial<FolderWithCollaborations> => {
  const updatedKeys = Object.keys(folder).filter(
    (k: keyof FolderWithCollaborations) => !isEqual(initialFolder[k], folder[k])
  );
  return updatedKeys.reduce(
    (o, key: keyof FolderWithCollaborations) => Object.assign(o, {[key]: folder[key]}),
    {}
  );
};

const _hasMadeEdits = (updateData: Partial<FolderWithCollaborations>): boolean =>
  Object.keys(updateData).length > 0;

export function FolderSettingsModal(props: FolderSettingsModalProps): JSX.Element | null {
  const [folder, updateFolder] = useState<Folder>({
    ...deepCopy(props.folder),
  });
  const [saving, updateSaving] = useState<boolean>(false);
  const [collaborationState, collaborationActions] = useCollaborationManagementState(props.folder.id);

  const updateData = _getUpdatedFolderObject(
    {
      ...props.folder,
      collaborations: collaborationState.initialCollaborations,
    },
    {
      ...folder,
      collaborations: collaborationState.collaborations,
    }
  );

  const save = (e: ElementMouseEvent): void => {
    e.preventDefault();
    updateSaving(true);

    FolderActions.updateFolder({...folder, parent: {api_identifier: folder.parentId}}, updateData)
      .then(() => {
        notifications.success('Folder successfully updated.');
        props.refetch();
        props.onRequestClose();
      })
      .catch(notifications.notifyError)
      .finally(() => {
        updateSaving(false);
      });
  };

  return (
    <Modal
      title={`Settings for "${props.folder.name}"`}
      onRequestClose={props.onRequestClose}
      buttons={[
        {
          disabled: !_hasMadeEdits(updateData) || saving || props.isReadOnly,
          onClick: save,
          submit: true,
          text: saving ? 'Saving...' : 'Save',
        },
      ]}
      wrapInForm={false}
      dataTestAttrs={{component: 'FolderSettingsModal', element: 'root'}}
    >
      <Row>
        <Col sm={12}>
          <FormGroup>
            <Row>
              <Col sm={6} md={7}>
                <Label>Name</Label>
                <FormInput
                  type="text"
                  placeholder="Enter a folder name"
                  value={folder.name}
                  onChange={(e: InputChangeEvent) => {
                    updateFolder({...folder, name: e.target.value});
                  }}
                  dataTestAttrs={{component: 'FolderSettingsModal', element: 'input', key: 'name'}}
                  disabled={props.isReadOnly}
                />
              </Col>
              <Col sm={6} md={5}>
                <Label>Location</Label>
                <FolderSelector
                  enableRedesign={true}
                  requireSubfolderCreatableAuth={true}
                  showInventory={folder.type === FolderType.INVENTORY}
                  showNotebook={folder.type === FolderType.NOTEBOOK}
                  value={folder.parentId}
                  onChange={(e) => {
                    if (e.target.value != null) {
                      updateFolder({...folder, parentId: e.target.value});
                    }
                  }}
                  dataTestAttrs={{component: 'FolderSettingsModal', element: 'folderSelector'}}
                  readOnly={props.isReadOnly}
                  unsetOnFetchFail={true}
                />
              </Col>
            </Row>
          </FormGroup>
          <FormGroup>
            <Label>Description</Label>
            <textarea
              placeholder="Enter a folder description"
              value={folder.description!}
              onChange={(e) => {
                updateFolder({...folder, description: e.target.value});
              }}
              {...getTestProps({
                component: 'FolderSettingsModal',
                element: 'descriptionText',
              })}
              disabled={props.isReadOnly}
            />
          </FormGroup>
        </Col>
      </Row>
      {BenchlingConfig.PERMISSIONS_ENABLE_GRANULAR_FOLDER_PERMISSIONS && (
        <Row>
          <Col sm={12}>
            <CollaborationManagementSection
              {...collaborationState}
              {...collaborationActions}
              policeableName={folder.name}
            />
          </Col>
        </Row>
      )}
    </Modal>
  );
}
